import { useEffect, useState } from "react";
import { Sparkles } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import type { SimBatchEvent } from "@opus-dt/shared-types";
import { useCancelAutoBatch } from "@/features/simulations/hooks";

interface AutoRunProgressProps {
  batchId: string;
  candidateTotal: number;
  onTerminal: () => void;
}

// Progresso ao vivo do lote ("Avaliando candidato X de N") via SSE + cancelar.
export function AutoRunProgress({ batchId, candidateTotal, onTerminal }: AutoRunProgressProps) {
  const [percent, setPercent] = useState(0);
  const [step, setStep] = useState("Na fila…");
  const cancel = useCancelAutoBatch();

  useEffect(() => {
    const es = new EventSource(`/api/v1/auto-simulations/${batchId}/progress`, {
      withCredentials: true,
    });
    let consecutiveErrors = 0;
    es.onmessage = (e) => {
      consecutiveErrors = 0; // recebeu dado → conexão saudável
      try {
        const ev = JSON.parse(e.data as string) as SimBatchEvent;
        setPercent(ev.percent);
        setStep(ev.step);
        if (ev.type !== "progress") {
          es.close();
          onTerminal();
        }
      } catch {
        /* heartbeat */
      }
    };
    // Erro transitório → deixa o EventSource reconectar; mas falha PERSISTENTE (lote
    // removido/403/404) não pode reconectar para sempre: após N tentativas seguidas, desiste
    // e deixa o poll do pai (useAutoBatch) refletir o estado terminal.
    es.onerror = () => {
      consecutiveErrors += 1;
      if (consecutiveErrors >= 5) {
        es.close();
        onTerminal();
      }
    };
    return () => es.close();
  }, [batchId, onTerminal]);

  return (
    <Card>
      <CardContent className="space-y-4 pt-6 text-center">
        <Sparkles className="mx-auto h-8 w-8 text-primary" />
        <h2 className="text-lg font-semibold">Gerando cenários</h2>
        <p className="text-sm text-muted-foreground">{step}</p>
        <div className="h-2 w-full overflow-hidden rounded-full bg-muted">
          <div
            className="h-full w-[var(--w)] bg-primary transition-all"
            style={{ "--w": `${percent}%` } as React.CSSProperties}
          />
        </div>
        <p className="text-xs text-muted-foreground">
          {percent}% · até {candidateTotal} candidatos · a linha real não é tocada
        </p>
        <Button
          variant="outline"
          size="sm"
          disabled={cancel.isPending}
          onClick={() => cancel.mutate(batchId)}
        >
          Cancelar busca
        </Button>
      </CardContent>
    </Card>
  );
}
